// src/context/UserContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo, useCallback } from 'react';
import {logoutUser} from "../../services/api";

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'collaborator' | 'admin';
  display_name?: string;
  profile_image?: string;
  creator_type?: string;
  bio?: string;
  social_profiles?: Array<{
    platform: string;
    handle: string;
    profile_url: string;
    follower_count?: string;
  }>;
  ai_match_enabled?: boolean;
  video_intro_url?: string;
}

export interface UserContextType {
  user: User | null;
  loading: boolean;
  login: (userData: User) => void;
  logout: () => void;
  updateUser: (userData: User) => void;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing user in localStorage
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Ensure user ID is always a number for consistent comparisons
        if (parsedUser && parsedUser.id) {
          parsedUser.id = typeof parsedUser.id === 'string' ? parseInt(parsedUser.id) : parsedUser.id;
        }
        setUser(parsedUser);
      } catch (e) {
        console.error('Failed to parse stored user data');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData: User) => {
    // Ensure user ID is always a number for consistent comparisons
    const normalizedUserData = {
      ...userData,
      id: typeof userData.id === 'string' ? parseInt(userData.id as string) : userData.id
    };
    setUser(normalizedUserData);
    localStorage.setItem('currentUser', JSON.stringify(normalizedUserData));
  }, []);

  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
    localStorage.removeItem('currentUser');
  }, []);

  const updateUser = useCallback((userData: User) => {
    // Ensure user ID is always a number for consistent comparisons
    const normalizedUserData = {
      ...userData,
      id: typeof userData.id === 'string' ? parseInt(userData.id as string) : userData.id
    };
    setUser(normalizedUserData);
    localStorage.setItem('currentUser', JSON.stringify(normalizedUserData));
  }, []);

  const register = useCallback(async (userData: any): Promise<{ success: boolean; error?: string }> => {
    try {
      // In a real implementation, this would call an API endpoint
      // For now, we'll just simulate a successful registration
      const newUser: User = {
        id: Date.now(), // temporary ID generation
        username: userData.username || userData.email,
        email: userData.email,
        role: userData.role || 'collaborator',
        display_name: userData.display_name,
        profile_image: userData.profile_image,
        creator_type: userData.creator_type,
        bio: userData.bio,
        social_profiles: userData.social_profiles || []
      };
      
      login(newUser);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  }, [login]);

  const value = useMemo(() => ({ user, loading, login, logout, updateUser, register }), [user, loading, login, logout, updateUser, register]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Safe version that returns null instead of throwing when outside UserProvider
export const useUserSafe = (): UserContextType | null => {
  const context = useContext(UserContext);
  return context ?? null;
};